// pages/MarketRequests.jsx
// DWI Market Phase 3 — "Buy requests": a buyer tells DWI what they want
// (product, quantity, target price, when, where, contact), DWI finds it and
// sends a quote, and the buyer accepts → a normal order with a pay link
// (DWIPay Wallet or Mobile Money) — or declines. Buyers only deal with DWI.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { marketAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Button, Card, Input, Select, Badge, Modal, Empty, Spinner } from '../components/ui';

const ugx = n => `UGX ${Number(n || 0).toLocaleString('en-US')}`;
const day = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export const REQUEST_STATUS = {
  new:       ['Sent to DWI', 'amber'],
  sourcing:  ['DWI is finding it', 'cyan'],
  quoted:    ['Quote ready', 'green'],
  won:       ['Accepted — see My orders', 'teal'],
  lost:      ['Closed', 'gray'],
  cancelled: ['Cancelled', 'gray'],
};
export const QUOTE_STATUS = {
  sent:      ['Waiting for your answer', 'green'],
  accepted:  ['Accepted', 'teal'],
  declined:  ['Declined', 'gray'],
  expired:   ['Expired', 'gray'],
  withdrawn: ['Withdrawn', 'gray'],
};

export default function RequestsTab({ contact }) {
  const [params, setParams] = useSearchParams();
  const [data, setData]     = useState(null);
  const [closed, setClosed] = useState(false);
  // Opened from a "price on request" product → the form starts pre-filled.
  const prefill = params.get('ask') ? {
    productName: params.get('name') || '', categoryId: params.get('category') || '',
    unitId: params.get('unit') || '', productId: params.get('product') || '',
  } : null;
  const [showForm, setShowForm] = useState(!!prefill);

  const load = useCallback(async () => {
    try { setData(await marketAPI.myRequests()); }
    catch (err) { if (err.status === 503) setClosed(true); else toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (closed) {
    return (
      <Card>
        <Empty icon="📝" title="Buy requests open soon" description="You'll be able to tell DWI what you need and get a price. Until then, contact DwiSend directly." />
        {contact?.whatsapp && (
          <div className="flex justify-center"><a href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent('Hello DwiSend, I want to buy: ')}`} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="success">WhatsApp DWI</Button></a></div>
        )}
      </Card>
    );
  }
  if (!data) return <div className="py-16 flex justify-center"><Spinner /></div>;

  return (
    <div className="space-y-3">
      {showForm
        ? <RequestForm options={data.options} prefill={prefill}
            onCancel={() => { setShowForm(false); setParams({ tab: 'requests' }); }}
            onSent={() => { setShowForm(false); setParams({ tab: 'requests' }); load(); }} />
        : (
          <Card>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-text-primary font-bold">Can't find it? Tell DWI what you need.</div>
                <div className="text-xs text-text-muted">Product, how much, your price, when and where. DWI finds it and sends you a quote to accept and pay.</div>
              </div>
              <Button size="sm" onClick={() => setShowForm(true)}>+ I want to buy</Button>
            </div>
          </Card>
        )}
      {data.requests.length === 0
        ? !showForm && <Card><Empty icon="📝" title="No requests yet" description="Schools, hostels, shops: ask for bulk staples, produce, fish or electricals." /></Card>
        : data.requests.map(r => <RequestCard key={r._id} r={r} contact={contact} onChanged={load} />)}
    </div>
  );
}

function RequestForm({ options, prefill, onCancel, onSent }) {
  const { user } = useAuth();
  const [f, setF] = useState(() => ({
    productName: prefill?.productName || '', categoryId: prefill?.categoryId || options.categories[0]?._id || '',
    unitId: prefill?.unitId || options.units[0]?._id || '', productId: prefill?.productId || '',
    quantity: '', targetPriceUGX: '', neededBy: '', deliveryZoneId: '', deliveryAddress: '',
    phone: user?.phone || '', whatsapp: '', notes: '',
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const unit = options.units.find(u => u._id === f.unitId);

  const send = async () => {
    if (options.zones.length && !f.deliveryZoneId) return toast.error('Choose your delivery area');
    setSaving(true);
    try {
      await marketAPI.createRequest({ ...f, quantity: Number(f.quantity), targetPriceUGX: f.targetPriceUGX === '' ? null : Number(f.targetPriceUGX),
        neededBy: f.neededBy || null, deliveryZoneId: f.deliveryZoneId || null, productId: f.productId || undefined });
      toast.success('Sent — DWI will reply with a quote');
      onSent();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Card>
      <div className="text-text-primary font-bold mb-3">I want to buy</div>
      <div className="space-y-3">
        <Input label="Product" value={f.productName} onChange={e => set('productName', e.target.value)} placeholder="e.g. Beans (Nambale), rice, tilapia, solar kit" />
        <div className="grid sm:grid-cols-2 gap-3">
          <Select label="Category" value={f.categoryId} onChange={e => set('categoryId', e.target.value)}>
            {options.categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
          <Select label="Size" value={f.unitId} onChange={e => set('unitId', e.target.value)}>
            {options.units.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </Select>
          <Input label={`Quantity (${unit?.name || 'units'})`} type="number" min="1" step="1" value={f.quantity} onChange={e => set('quantity', e.target.value)} />
          <Input label={`Your target price per ${unit?.name || 'unit'} (optional)`} type="number" min="1" step="1" value={f.targetPriceUGX} onChange={e => set('targetPriceUGX', e.target.value)} />
          <Input label="Needed by (optional)" type="date" value={f.neededBy} onChange={e => set('neededBy', e.target.value)} />
          {options.zones.length > 0 && (
            <Select label="Delivery area" value={f.deliveryZoneId} onChange={e => set('deliveryZoneId', e.target.value)}>
              <option value="">— choose —</option>
              {options.zones.map(z => <option key={z._id} value={z._id}>{z.name}{z.notes ? ` (${z.notes})` : ''}</option>)}
            </Select>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary">Delivery address</label>
          <textarea rows={2} maxLength={500} value={f.deliveryAddress} onChange={e => set('deliveryAddress', e.target.value)} placeholder="School / hostel / shop name, road, landmark"
            className="mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label="Phone" value={f.phone} onChange={e => set('phone', e.target.value)} placeholder="07XXXXXXXX" />
          <Input label="WhatsApp (optional, with country code)" value={f.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="2567XXXXXXXX" />
        </div>
        <Input label="Notes (optional)" value={f.notes} onChange={e => set('notes', e.target.value)} placeholder="Grade, variety, packing, delivery days, repeat every term…" />
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button loading={saving} onClick={send}>Send to DWI</Button>
        </div>
      </div>
    </Card>
  );
}

function RequestCard({ r, contact, onChanged }) {
  const navigate = useNavigate();
  const [label, color] = REQUEST_STATUS[r.status];
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm(`Cancel your request for ${r.productName}?`)) return;
    setBusy(true);
    try { await marketAPI.cancelRequest(r._id); toast.success('Request cancelled'); onChanged(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-text-primary font-bold">{r.productName}</div>
          <div className="text-[11px] text-text-muted">{r.ref} · {r.categoryName} · asked {when(r.createdAt)}</div>
        </div>
        <Badge label={label} color={color} />
      </div>
      <div className="text-sm mt-2">
        {r.quantity.toLocaleString()} × {r.unitName}{r.targetPriceUGX ? <> · your price {ugx(r.targetPriceUGX)} each</> : null}
        {r.neededBy ? <> · by {day(r.neededBy)}</> : null}
      </div>
      <div className="text-[11px] text-text-muted">{r.deliveryZoneName ? `${r.deliveryZoneName} · ` : ''}{r.deliveryAddress}</div>
      {r.status === 'lost' && r.statusReason && <div className="text-xs text-text-muted mt-1">DWI: {r.statusReason}</div>}
      {r.quotes.map(q => <QuoteBox key={q._id} q={q} r={r} onChanged={onChanged} />)}
      <div className="flex gap-2 flex-wrap mt-3">
        {['new', 'sourcing', 'quoted'].includes(r.status) && <Button size="xs" variant="ghost" loading={busy} onClick={cancel}>Cancel request</Button>}
        <Button size="xs" variant="secondary" onClick={() => navigate(`/market?tab=messages&topic=buying&request=${r._id}&subject=${encodeURIComponent(`${r.productName} (${r.ref})`)}`)}>💬 Message DWI</Button>
        {contact?.whatsapp && (
          <a href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent(`Hello DwiSend, about my request ${r.ref} (${r.productName}): `)}`} target="_blank" rel="noopener noreferrer">
            <Button size="xs" variant="success">WhatsApp DWI</Button>
          </a>
        )}
      </div>
    </Card>
  );
}

function QuoteBox({ q, r, onChanged }) {
  const navigate = useNavigate();
  const [label, color] = QUOTE_STATUS[q.status] || [q.status, 'gray'];
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const live = q.status === 'sent';
  return (
    <div className={`mt-3 rounded-xl border p-3 ${live ? 'border-green/50 bg-green/5' : 'border-border'}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-bold text-text-primary">Quote {q.ref}</div>
        <Badge label={label} color={color} />
      </div>
      <div className="mt-2 space-y-0.5 text-sm">
        {q.lines.map(l => <div key={l._id} className="flex justify-between gap-3"><span>{l.qty.toLocaleString()} × {l.name} ({l.unitName}) @ {ugx(l.unitPriceUGX)}</span><span>{ugx(l.lineTotalUGX)}</span></div>)}
        {q.deliveryFeeUGX > 0 && <div className="flex justify-between gap-3 text-text-muted"><span>Delivery{q.deliveryZoneName ? ` (${q.deliveryZoneName})` : ''}</span><span>{ugx(q.deliveryFeeUGX)}</span></div>}
        <div className="flex justify-between gap-3 font-bold text-text-primary border-t border-border pt-1"><span>Total</span><span>{ugx(q.totalUGX)}</span></div>
      </div>
      {q.terms && <div className="text-xs text-text-secondary mt-2 whitespace-pre-line">Terms: {q.terms}</div>}
      <div className="text-[11px] text-text-muted mt-1">{live ? `Valid until ${when(q.validUntil)}` : q.respondedAt ? `Answered ${when(q.respondedAt)}` : ''}</div>
      {q.order && <div className="text-xs mt-1">Order <span className="font-mono text-cyan">{q.order.orderRef}</span> — <button className="text-cyan underline" onClick={() => navigate('/market?tab=orders')}>see My orders</button></div>}
      {live && (
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="success" onClick={() => setAccepting(true)}>Accept & pay</Button>
          <Button size="sm" variant="ghost" onClick={() => setDeclining(true)}>Decline</Button>
        </div>
      )}
      {accepting && <AcceptModal q={q} r={r} onClose={() => setAccepting(false)} onDone={payUrl => navigate(payUrl)} />}
      {declining && <DeclineModal q={q} onClose={() => setDeclining(false)} onDone={() => { setDeclining(false); onChanged(); }} />}
    </div>
  );
}

function AcceptModal({ q, r, onClose, onDone }) {
  const [phone, setPhone] = useState(r.phone);
  const [address, setAddress] = useState(r.deliveryAddress);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const res = await marketAPI.acceptQuote(q._id, { phone, deliveryAddress: address });
      toast.success(`Order ${res.order.orderRef} created — complete payment`);
      onDone(res.payUrl);
    } catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Accept quote ${q.ref}`} size="sm">
      <div className="space-y-3">
        <div className="text-sm">You'll pay <b className="text-text-primary">{ugx(q.totalUGX)}</b> to DWI on the next screen, with DWIPay Wallet or Mobile Money.</div>
        <Input label="Phone for delivery" value={phone} onChange={e => setPhone(e.target.value)} />
        <div>
          <label className="text-xs font-semibold text-text-secondary">Delivery address</label>
          <textarea rows={2} maxLength={500} value={address} onChange={e => setAddress(e.target.value)}
            className="mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan" />
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Back</Button>
          <Button className="flex-1" variant="success" loading={busy} onClick={go}>Accept & pay</Button>
        </div>
      </div>
    </Modal>
  );
}

function DeclineModal({ q, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await marketAPI.declineQuote(q._id, note); toast.success('Quote declined — DWI is told'); onDone(); }
    catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Decline quote ${q.ref}`} size="sm">
      <div className="space-y-3">
        <Input label="Why? (optional — helps DWI send a better offer)" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. price too high, need it sooner" />
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>Back</Button>
          <Button className="flex-1" loading={busy} onClick={go}>Decline</Button>
        </div>
      </div>
    </Modal>
  );
}
